import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Banner, Button, DataTable, Field, Input, Pill, Select, Td, Textarea } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { useSession } from '../../lib/session';
import type { TabProps } from './types';

const STATUSES = ['sent', 'contacted', 'retained', 'resolved'] as const;

const TONE = { sent: 'watch', contacted: 'watch', retained: 'ok', resolved: 'ok' } as const;

/**
 * Lienor-only: send this job file to named counsel. Owner never sees the firm.
 * Not a platform default inbox. Not a retain.
 */
export default function ReferralTab({ job, reload }: TabProps) {
  const { contractor } = useSession();
  // LienGuardian emails the firm in the company's name, so it needs a verified person.
  const verified = !!contractor?.personVerifiedAt;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState({ notes: '', firmEmail: '' });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/jobs/${job.id}/referral`, {
        notes: draft.notes || undefined,
        firmEmail: draft.firmEmail,
      });
      setDraft({ notes: '', firmEmail: '' });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not submit the referral');
    } finally {
      setBusy(false);
    }
  };

  const update = async (id: string, status: string) => {
    setError(null);
    try {
      await api.put(`/api/jobs/${job.id}/referral/${id}`, { status });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update the referral');
    }
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_20rem]">
      <div className="space-y-4">
        {error && <Banner tone="urgent">{error}</Banner>}
        <DataTable head={['Submitted', 'Status', 'Attorney contacted', 'Resolved', 'Update']} empty="No referrals on this job.">
          {job.referrals.map((r) => (
            <tr key={r.id} className="border-b border-border/60">
              <Td className="text-muted">{fmtDateTime(r.submittedAt)}</Td>
              <Td>
                <Pill tone={TONE[r.status as keyof typeof TONE] ?? 'none'}>{r.status}</Pill>
              </Td>
              <Td className="text-muted">{fmtDateTime(r.attorneyContactedAt)}</Td>
              <Td className="text-muted">{fmtDateTime(r.resolvedAt)}</Td>
              <Td>
                <Select
                  aria-label="Update referral status"
                  value={r.status}
                  onChange={(e) => void update(r.id, e.target.value)}
                  className="w-36 py-1 text-xs"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </Td>
            </tr>
          ))}
        </DataTable>
      </div>

      <form onSubmit={submit} className="space-y-4 rounded-lg border border-border bg-surface-alt/40 p-4">
        <h3 className="text-sm font-semibold text-ink">Send file to your attorney</h3>
        <p className="text-xs text-muted">
          Your counsel. The owner does not see this. LienGuardian is not a law firm and does not pick or retain anyone. We send clocks, mail proof, invoices, and the Claim of Lien draft if one exists — counsel decides whether to file.
        </p>
        {!verified && (
          <Banner tone="watch" title="Verify who you are to send the file">
            LienGuardian emails your attorney in your company’s name, so it needs a verified person first.{' '}
            <Link to="/settings" className="underline">
              Verify in Settings
            </Link>
            .
          </Banner>
        )}
        <Field label="Firm email" hint="Required. Named counsel for this job only.">
          <Input type="email" required value={draft.firmEmail} onChange={(e) => setDraft({ ...draft, firmEmail: e.target.value })} />
        </Field>
        <Field label="Notes for the attorney">
          <Textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
        </Field>
        <Button type="submit" disabled={busy || !draft.firmEmail || !verified} className="w-full">
          {busy ? 'Sending…' : 'Send file to counsel'}
        </Button>
      </form>
    </div>
  );
}
